import { UNITS_OF_MEASURE } from './constants';
import {
    createCompanyUnitOfMeasure,
    listCompanyUnitsOfMeasureByCompanyId,
    type CompanyUnitOfMeasureRecord,
} from '../graphql/companyUnitOfMeasureQueries';
import { catalogDisplayNameForUnitOfMeasure, normalizeUnitOfMeasure } from './unitOfMeasureUtils';

const EXTRA_SORT_START = 100;

/** Serialize seed per company so concurrent picker/tab loads cannot duplicate presets. */
const seedByCompanyId = new Map<string, Promise<CompanyUnitOfMeasureRecord[]>>();

/** Shared identity for spellings of one measure, such as Each and Ea. */
export function equivalentUnitOfMeasureKey(value: string): string {
    return normalizeUnitOfMeasure(value) || value.trim().toLowerCase();
}

export function unitUsesStoredMeasure(
    stored: string | null | undefined,
    catalogValue: string,
): boolean {
    if (!stored?.trim() || !catalogValue?.trim()) return false;
    return equivalentUnitOfMeasureKey(stored) === equivalentUnitOfMeasureKey(catalogValue);
}

/** One catalog row per measure. Each wins over Ea when both exist. */
export function collapseEquivalentUnitsOfMeasure<T extends { name: string; value: string }>(items: T[]): T[] {
    const groups = new Map<string, T[]>();
    for (const item of items) {
        const key = equivalentUnitOfMeasureKey(item.value);
        const group = groups.get(key);
        if (group) group.push(item);
        else groups.set(key, [item]);
    }
    return [...groups.values()].map((group) => {
        const canonical = group.find((item) => item.value === equivalentUnitOfMeasureKey(item.value));
        return canonical ?? group[0];
    });
}

export type UnitOfMeasureUsage = {
    equipment: number;
    schedules: number;
    serviceItems: number;
};

type EquipmentMeasure = { id: string; unitOfMeasure?: string | null };
type ScheduleMeasure = { unitId?: string | null; unitOfMeasure?: string | null };
type ServiceItemMeasure = { unitOfMeasure?: string | null };

/**
 * Equipment meters, PM schedules that use a different measure than the equipment
 * record, and service items. A schedule already covered by the equipment meter
 * is not counted again.
 */
export function countUnitOfMeasureUsage(
    catalogValue: string,
    sources: {
        equipment: EquipmentMeasure[];
        schedules: ScheduleMeasure[];
        serviceItems: ServiceItemMeasure[];
    },
): UnitOfMeasureUsage {
    const equipment = sources.equipment.filter((unit) =>
        unitUsesStoredMeasure(unit.unitOfMeasure, catalogValue),
    );
    const equipmentIds = new Set(equipment.map((unit) => unit.id));
    const schedules = sources.schedules.filter(
        (schedule) =>
            unitUsesStoredMeasure(schedule.unitOfMeasure, catalogValue) &&
            !equipmentIds.has(schedule.unitId || ''),
    ).length;
    const serviceItems = sources.serviceItems.filter((item) =>
        unitUsesStoredMeasure(item.unitOfMeasure, catalogValue),
    ).length;
    return { equipment: equipment.length, schedules, serviceItems };
}

export function unitOfMeasureUsageTotal(usage: UnitOfMeasureUsage): number {
    return usage.equipment + usage.schedules + usage.serviceItems;
}

export function formatUnitOfMeasureUsage(usage: UnitOfMeasureUsage): string {
    const parts: string[] = [];
    if (usage.equipment > 0) {
        parts.push(`${usage.equipment} equipment`);
    }
    if (usage.schedules > 0) {
        parts.push(`${usage.schedules} ${usage.schedules === 1 ? 'schedule' : 'schedules'}`);
    }
    if (usage.serviceItems > 0) {
        parts.push(
            `${usage.serviceItems} ${usage.serviceItems === 1 ? 'service item' : 'service items'}`,
        );
    }
    return parts.length > 0 ? parts.join(', ') : 'Not used';
}

async function seedCompanyUnitsOfMeasureNow(
    companyId: string,
    extraValues: string[] = [],
): Promise<CompanyUnitOfMeasureRecord[]> {
    const existing = await listCompanyUnitsOfMeasureByCompanyId(companyId);
    const byValue = new Set(existing.map((item) => item.value));

    // Presets only on first load. Re-adding missing presets would undo a Resources delete.
    if (existing.length === 0) {
        for (let index = 0; index < UNITS_OF_MEASURE.length; index += 1) {
            const preset = UNITS_OF_MEASURE[index];
            if (byValue.has(preset.value)) continue;
            const created = await createCompanyUnitOfMeasure({
                companyId,
                name: preset.label,
                value: preset.value,
                sortOrder: index,
            });
            existing.push(created);
            byValue.add(created.value);
        }
    }

    let extraOrder = Math.max(
        EXTRA_SORT_START,
        ...existing.map((item) => item.sortOrder ?? EXTRA_SORT_START),
    );

    for (const raw of extraValues) {
        const value = normalizeUnitOfMeasure(raw);
        if (!value || byValue.has(value)) continue;
        const created = await createCompanyUnitOfMeasure({
            companyId,
            name: catalogDisplayNameForUnitOfMeasure(raw),
            value,
            sortOrder: extraOrder,
        });
        extraOrder += 1;
        existing.push(created);
        byValue.add(created.value);
    }

    return existing.sort((a, b) => {
        const orderA = a.sortOrder ?? 9999;
        const orderB = b.sortOrder ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
    });
}

/**
 * Ensure the company has the preset UM catalog (and any extra values already
 * stored on equipment). Safe to call on every picker/tab load.
 */
export async function ensureCompanyUnitsOfMeasureSeeded(
    companyId: string,
    extraValues: string[] = [],
): Promise<CompanyUnitOfMeasureRecord[]> {
    if (!companyId) return [];

    const next = (seedByCompanyId.get(companyId) || Promise.resolve([]))
        .catch(() => [] as CompanyUnitOfMeasureRecord[])
        .then(() => seedCompanyUnitsOfMeasureNow(companyId, extraValues));
    seedByCompanyId.set(companyId, next);
    return next;
}

export async function addCompanyUnitOfMeasureFromLabel(
    companyId: string,
    rawLabel: string,
    existing: CompanyUnitOfMeasureRecord[] = [],
): Promise<CompanyUnitOfMeasureRecord> {
    const name = rawLabel.trim();
    if (!name) {
        throw new Error('Unit of measure name is required.');
    }
    const value = normalizeUnitOfMeasure(name);
    const catalog =
        existing.length > 0 ? existing : await listCompanyUnitsOfMeasureByCompanyId(companyId);
    const match = catalog.find((item) => item.value === value);
    if (match) return match;

    const latest = await listCompanyUnitsOfMeasureByCompanyId(companyId);
    const latestMatch = latest.find((item) => item.value === value);
    if (latestMatch) return latestMatch;

    const maxOrder = latest.reduce((max, item) => Math.max(max, item.sortOrder ?? 0), EXTRA_SORT_START);
    return createCompanyUnitOfMeasure({
        companyId,
        name: catalogDisplayNameForUnitOfMeasure(name),
        value,
        sortOrder: maxOrder + 1,
    });
}
